import * as THREE from 'three';
import type { MapDef } from '@somnhold/sim';
import { WORLD_PER_HEX } from './coords.ts';
import { walledDist } from './wallgraph.ts';

/*
 * Era V, Somnhold (docs/ERAS.md): what the bastion shows around the keep once the fortress is whole again.
 * - The titan's shell breaks through the turf outside the walls: big hexagonal plates of its dark stone, a few with
 *   a seam of pale light (two instanced draws for all of them).
 * - The Dream Beacon on the keep beats slowly: a soft column of blue-white light up into the sky, a halo that swells
 *   with each beat and a ring of light that runs out over the board from the keep's foot.
 * - A dream aurora hangs over the far side of the board (one open cylinder, faded where it would cross the view).
 * Cosmetic only, no point lights (docs/RENDIMIENTO.md): the keep's brazier light is the one that beats.
 */

/** The look of the era V sky when no holiday or omen dresses it: a colder, bluer night of dreams. */
export const SOMN_SKY = {
  hemi: ['#a9b6da', '#262a3c', 2.4] as const,
  key: ['#d6defc', 3.1] as const,
  rim: ['#a8bcff', 2.2] as const,
  background: '#2a3040',
  fog: '#2a3040',
  mist: { low: '#3b4357', mid: '#323a4d', far: '#2a3040', alpha: 1.0 },
  brazier: 0xcfe0ff,
};

const BEAM_VERT = /* glsl */ `
  varying float vY;
  varying vec3 vN;
  varying vec3 vW;
  void main() {
    vY = uv.y;
    vec4 w = modelMatrix * vec4(position, 1.0);
    vW = w.xyz;
    vN = normalize(mat3(modelMatrix) * normal);
    gl_Position = projectionMatrix * viewMatrix * w;
  }`;
const BEAM_FRAG = /* glsl */ `
  uniform float uBeat;
  uniform vec3 uColor;
  varying float vY;
  varying vec3 vN;
  varying vec3 vW;
  void main() {
    // Bright at the lens, fading up into the sky; softer at the column's edges.
    vec3 v = normalize(cameraPosition - vW);
    float edge = pow(abs(dot(normalize(vN), v)), 1.5);
    float a = pow(1.0 - vY, 2.2) * edge * (0.28 + 0.32 * uBeat);
    gl_FragColor = vec4(uColor * a, a);
  }`;

const RING_FRAG = /* glsl */ `
  uniform float uFade;
  uniform vec3 uColor;
  varying vec2 vUv;
  void main() {
    float d = abs(vUv.y - 0.5) * 2.0;
    float a = (1.0 - d * d) * uFade;
    gl_FragColor = vec4(uColor * a, a);
  }`;
const RING_VERT = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }`;

const AURORA_VERT = /* glsl */ `
  varying vec2 vUv;
  varying vec3 vW;
  void main() {
    vUv = uv;
    vec4 w = modelMatrix * vec4(position, 1.0);
    vW = w.xyz;
    gl_Position = projectionMatrix * viewMatrix * w;
  }`;
const AURORA_FRAG = /* glsl */ `
  uniform float uTime;
  uniform float uAlpha;
  varying vec2 vUv;
  varying vec3 vW;
  void main() {
    // Only the far side of the ring: the curtain behind the board, never across it.
    vec2 toCam = normalize(cameraPosition.xz);
    vec2 here = normalize(vW.xz);
    float far = smoothstep(0.05, 0.65, -dot(here, toCam));
    float x = vUv.x * 6.2831853 * 7.0;
    // Folding curtains: rays that drift sideways, brighter where the folds meet.
    float fold = sin(x + sin(x * 0.37 + uTime * 0.21) * 2.2 + uTime * 0.08);
    float rays = 0.55 + 0.45 * sin(x * 5.3 + fold * 3.0 + uTime * 0.35);
    float body = smoothstep(0.0, 0.25, vUv.y) * (1.0 - smoothstep(0.45, 1.0, vUv.y + fold * 0.12));
    vec3 low = vec3(0.37, 0.82, 0.76);
    vec3 mid = vec3(0.56, 0.70, 1.0);
    vec3 high = vec3(0.70, 0.55, 1.0);
    vec3 c = mix(low, mid, smoothstep(0.1, 0.5, vUv.y));
    c = mix(c, high, smoothstep(0.5, 0.95, vUv.y));
    float a = body * rays * far * uAlpha * (0.75 + 0.25 * fold);
    gl_FragColor = vec4(c * a, a);
  }`;

const prng = (seed: number): (() => number) => {
  let s = seed >>> 0 || 1;
  return () => {
    s = (Math.imul(s ^ (s >>> 15), 2246822519) + 0x9e3779b9) >>> 0;
    return s / 4294967296;
  };
};

export interface SomnCtx {
  map: MapDef;
  seed: number;
  low: boolean;
  /** Ground height outside the tiles, and the distance to the nearest road (both world units). */
  ground?: (x: number, z: number) => number;
  roadDist?: (x: number, z: number) => number;
  /** The Dream Beacon's lens, in world space. */
  lens: THREE.Vector3;
  /** The keep's foot. */
  foot: THREE.Vector3;
}

export class SomnLayer {
  readonly group = new THREE.Group();
  /** 0..1, the beacon's beat this frame (the view beats the keep's light with it). */
  beat = 0;
  private readonly beam: THREE.ShaderMaterial;
  private readonly halo: THREE.Sprite;
  private readonly wave: THREE.Mesh;
  private readonly waveMat: THREE.ShaderMaterial;
  private readonly aurora: THREE.ShaderMaterial;
  private readonly owned: { dispose(): void }[] = [];

  constructor(ctx: SomnCtx) {
    this.group.name = 'somnhold';
    this.plates(ctx);
    // The beam: a tall open cone from the lens up into the sky.
    const H = 16;
    const beamGeo = new THREE.CylinderGeometry(0.9, 0.12, H, 20, 1, true).translate(0, H / 2, 0);
    this.beam = new THREE.ShaderMaterial({
      vertexShader: BEAM_VERT,
      fragmentShader: BEAM_FRAG,
      uniforms: { uBeat: { value: 0 }, uColor: { value: new THREE.Color('#bcd8ff') } },
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
    });
    // Its uv.v runs 0 at the bottom to 1 at the top.
    const beam = new THREE.Mesh(beamGeo, this.beam);
    beam.position.copy(ctx.lens);
    beam.name = 'somn-beam';
    // The halo round the lens: a glow that swells with each beat.
    const tex = haloTexture();
    const haloMat = new THREE.SpriteMaterial({ map: tex, color: '#d6e8ff', transparent: true, depthWrite: false, blending: THREE.AdditiveBlending });
    this.halo = new THREE.Sprite(haloMat);
    this.halo.position.copy(ctx.lens);
    // A ring of light that runs out from the keep's foot with each beat.
    this.waveMat = new THREE.ShaderMaterial({
      vertexShader: RING_VERT,
      fragmentShader: RING_FRAG,
      uniforms: { uFade: { value: 0 }, uColor: { value: new THREE.Color('#a8ccff') } },
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    // RingGeometry's uv is planar: v is rewritten to run across the ring's width (0 inside, 1 outside).
    const ringGeo = new THREE.RingGeometry(0.92, 1, 64, 1).rotateX(-Math.PI / 2);
    const pos = ringGeo.getAttribute('position');
    const uv = ringGeo.getAttribute('uv');
    for (let i = 0; i < pos.count; i++) uv.setY(i, (Math.hypot(pos.getX(i), pos.getZ(i)) - 0.92) / 0.08);
    this.wave = new THREE.Mesh(ringGeo, this.waveMat);
    this.wave.position.copy(ctx.foot).setY(ctx.foot.y + 0.06);
    this.wave.renderOrder = 2;
    // The aurora over the far side of the board.
    const R = (ctx.map.bastion + 5.5) * WORLD_PER_HEX;
    const aurGeo = new THREE.CylinderGeometry(R * 1.06, R, 8, 96, 1, true).translate(0, 4 - 0.4, 0);
    this.aurora = new THREE.ShaderMaterial({
      vertexShader: AURORA_VERT,
      fragmentShader: AURORA_FRAG,
      uniforms: { uTime: { value: 0 }, uAlpha: { value: 0.9 } },
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
    });
    const aurora = new THREE.Mesh(aurGeo, this.aurora);
    aurora.name = 'somn-aurora';
    aurora.frustumCulled = false;
    this.group.add(beam, this.halo, this.wave, aurora);
    this.owned.push(beamGeo, this.beam, tex, haloMat, ringGeo, this.waveMat, aurGeo, this.aurora);
  }

  /**
   * The titan's shell plates in the turf: a lattice of big hexagons outside the walls (kept off the roads and the walls'
   * foot), each sunk a little and tilted, darker toward the fog. A third of them carry a seam of pale light.
   */
  private plates(ctx: SomnCtx): void {
    const { map, ground, roadDist } = ctx;
    if (!ground || !roadDist) return;
    const dist = walledDist(map);
    const B = map.bastion;
    const rnd = prng(ctx.seed ^ 0x50a1);
    const step = 2.05;
    const lim = (map.radius + 4) * WORLD_PER_HEX;
    const mats: THREE.Matrix4[] = [];
    const cols: THREE.Color[] = [];
    const seams: THREE.Matrix4[] = [];
    const palette = ['#3a3f47', '#424852', '#353a41', '#474d55'].map((c) => new THREE.Color(c));
    const q = new THREE.Quaternion();
    const e = new THREE.Euler();
    for (let j = -Math.ceil(lim / (step * 0.866)); j * step * 0.866 <= lim; j++) {
      for (let i = -Math.ceil(lim / step) - 1; i * step <= lim; i++) {
        const x = (i + (j & 1 ? 0.5 : 0)) * step + (rnd() - 0.5) * 0.25;
        const z = j * step * 0.866 + (rnd() - 0.5) * 0.25;
        const d = dist(x, z);
        if (d < B + 1.4 || d > B + 9.5) continue;
        const pd = roadDist(x, z);
        if (pd < 1.35) continue;
        const r = Math.min(0.98, 0.72 + rnd() * 0.24, (pd - 0.4) * 0.9);
        if (r < 0.5 || rnd() < 0.12) continue;
        const h = ground(x, z);
        const lift = 0.06 + rnd() * 0.12;
        e.set((rnd() - 0.5) * 0.12, Math.PI / 6 + (rnd() - 0.5) * 0.2, (rnd() - 0.5) * 0.12);
        q.setFromEuler(e);
        const m = new THREE.Matrix4().compose(new THREE.Vector3(x, h + lift - 0.14, z), q, new THREE.Vector3(r, 1, r));
        mats.push(m);
        cols.push((palette[Math.floor(rnd() * palette.length)] as THREE.Color).clone().multiplyScalar(0.92 + rnd() * 0.16));
        if (rnd() < 0.34) seams.push(new THREE.Matrix4().compose(new THREE.Vector3(x, h + lift + 0.005, z), q, new THREE.Vector3(r * 0.86, 1, r * 0.86)));
      }
    }
    if (mats.length === 0) return;
    // A thick hexagonal slab, bevelled on top, its top face at y = 0.14 (it is sunk by that much and lifted a little).
    const geo = new THREE.CylinderGeometry(0.84, 1, 0.28, 6, 1);
    const mat = new THREE.MeshStandardMaterial({ roughness: 0.9, metalness: 0.05, flatShading: true });
    const im = new THREE.InstancedMesh(geo, mat, mats.length);
    im.name = 'somn-plates';
    mats.forEach((m, k) => {
      im.setMatrixAt(k, m);
      im.setColorAt(k, cols[k] as THREE.Color);
    });
    im.instanceMatrix.needsUpdate = true;
    if (im.instanceColor) im.instanceColor.needsUpdate = true;
    im.computeBoundingSphere();
    this.group.add(im);
    this.owned.push(geo, mat);
    if (seams.length === 0) return;
    // The seams: a thin hexagon of pale light inset on the plate's top.
    const sgeo = new THREE.RingGeometry(0.62, 0.7, 6, 1).rotateX(-Math.PI / 2).rotateY(Math.PI / 6);
    const smat = new THREE.MeshBasicMaterial({ color: '#8fbcf0' });
    const sm = new THREE.InstancedMesh(sgeo, smat, seams.length);
    sm.name = 'somn-seams';
    seams.forEach((m, k) => sm.setMatrixAt(k, m));
    sm.instanceMatrix.needsUpdate = true;
    sm.computeBoundingSphere();
    this.group.add(sm);
    this.owned.push(sgeo, smat);
  }

  /** Every animation frame (seconds). One beat every 4 s: a swell of light, then the ring runs out. */
  frame(t: number): void {
    const ph = (t % 4) / 4;
    // A soft double pulse, like a slow heart.
    const beat = Math.exp(-((ph - 0.08) ** 2) / 0.004) + 0.5 * Math.exp(-((ph - 0.24) ** 2) / 0.004);
    this.beat = Math.min(1, beat);
    this.beam.uniforms.uBeat!.value = this.beat;
    this.halo.scale.setScalar(2.4 + 1.6 * this.beat);
    (this.halo.material as THREE.SpriteMaterial).opacity = 0.45 + 0.4 * this.beat;
    const run = Math.max(0, ph - 0.08) / 0.92;
    this.wave.scale.set(0.6 + run * 11, 1, 0.6 + run * 11);
    this.waveMat.uniforms.uFade!.value = run <= 0 ? 0 : (1 - run) * (1 - run) * 0.55;
    this.aurora.uniforms.uTime!.value = t % 3600;
  }

  dispose(): void {
    for (const o of this.owned) o.dispose();
  }
}

/** A soft round glow (generated). */
const haloTexture = (): THREE.Texture => {
  const n = 64;
  const data = new Uint8Array(n * n * 4);
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      const d = Math.hypot(x - n / 2 + 0.5, y - n / 2 + 0.5) / (n / 2);
      const a = Math.max(0, 1 - d) ** 2.2;
      const k = (y * n + x) * 4;
      data[k] = 255;
      data[k + 1] = 255;
      data[k + 2] = 255;
      data[k + 3] = Math.round(a * 255);
    }
  }
  const tex = new THREE.DataTexture(data, n, n);
  tex.needsUpdate = true;
  return tex;
};
